import { useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer, TroubleSection, PageHero } from "../components/SharedComponents";
import { FaCheck, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const plans = [
  {
    name: "Basic Plan",
    price: "299",
    cta: "Get Started",
    features: [
      "Monthly Bookkeeping",
      "Quarterly Bank Reconciliation",
      "Virtual Online Support",
      "Secured Client Doc Storage Portal"
    ],
    desc: "Perfect for small businesses and startups needing reliable foundational financial services at an affordable rate.",
    style: "basic"
  },
  {
    name: "Standard Plan",
    price: "799",
    recommended: true,
    cta: "Consultation Now",
    features: [
      "All Basic Plan",
      "Monthly Bank Reconciliation",
      "Sales Tax Filing and Compliance",
      "Payroll for Small Business (including Form 940 & Form 941)",
      "Rental Property & Depreciation Tracking",
      "Quarterly Financial Reports and Analysis",
      "Phone Consultation and Support"
    ],
    desc: "Ideal for growing businesses that need more comprehensive financial management and strategic advisory support.",
    style: "standard"
  },
  {
    name: "Premium Plan",
    price: "1499",
    cta: "Schedule Strategy Session",
    features: [
      "All Standard Plan",
      "Advanced Payroll Services",
      "Accounts Receivable Management",
      "Inventory Management and Analysis",
      "Budgeting and Forecasting",
      "Tax Audit and Prep Services"
    ],
    desc: "Designed for established businesses requiring full-spectrum financial services with dedicated expert support.",
    style: "premium"
  }
];

const PricingPage = () => {
  const [currentPlan, setCurrentPlan] = useState(1); // Start with Standard (recommended)

  const handlePrevPlan = () => {
    setCurrentPlan((prev) => (prev === 0 ? plans.length - 1 : prev - 1));
  };

  const handleNextPlan = () => {
    setCurrentPlan((prev) => (prev === plans.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="site-wrapper">
      <Header />
      <PageHero
        title="Pricing"
        bg="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1600&auto=format&fit=crop&q=80"
      />

      <section className="pricing-section">
        <div className="container">
          {/* AFSP Badge */}
          <div className="pricing-afsp-badge">
            <a href="https://www.irs.gov/tax-professionals/annual-filing-season-program" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://www.irs.gov/pub/irs-utl/afsp-web.jpg" 
                alt="IRS Annual Filing Season Program Participant"
                className="afsp-badge-img"
              />
            </a>
          </div>

          <p className="section-label" style={{ textAlign: "center" }}>Our Best Offer</p>
          <h2 className="pricing-main-title">
            Expert Services, <span className="text-blue">Competitive Rates</span>
          </h2>
          <p className="pricing-subtitle">
            Choose the plan that best fits your business needs. All plans include access to our expert team, secure cloud-based systems, and dedicated client support. Contact us to customize any plan for your specific requirements.
          </p>

          {/* Mobile Slider View */}
          <div className="pricing-mobile-slider">
            <button className="pricing-slider-btn prev" onClick={handlePrevPlan}>
              <FaChevronLeft />
            </button>
            
            <div className="pricing-slider-container">
              <div className={`pricing-card ${plans[currentPlan].style}`}>
                {plans[currentPlan].recommended && <div className="pricing-recommended-badge">Recommended</div>}
                <div className="pricing-card-header">
                  <h3>{plans[currentPlan].name}</h3>
                  <div className="pricing-price">
                    <span className="price-dollar">$</span>
                    <span className="price-num">{plans[currentPlan].price}</span>
                    <span className="price-period">/month*</span>
                  </div>
                </div>
                <div className="pricing-card-body">
                  <ul className="pricing-features">
                    {plans[currentPlan].features.map((f, fi) => (
                      <li key={fi}><FaCheck className="pricing-check" /> {f}</li>
                    ))}
                  </ul>
                  <p className="pricing-desc">{plans[currentPlan].desc}</p>
                  <a href="/#contact" className="btn pricing-btn">{plans[currentPlan].cta}</a>
                </div>
              </div>
            </div>

            <button className="pricing-slider-btn next" onClick={handleNextPlan}>
              <FaChevronRight />
            </button>
            
            <div className="pricing-slider-dots">
              {plans.map((_, i) => (
                <button
                  key={i}
                  className={`slider-dot ${i === currentPlan ? 'active' : ''}`}
                  onClick={() => setCurrentPlan(i)}
                />
              ))}
            </div>
          </div>

          {/* Desktop Grid View */}
          <div className="pricing-grid">
            {plans.map((plan, i) => (
              <div key={i} className={`pricing-card ${plan.style}`}>
                {plan.recommended && <div className="pricing-recommended-badge">Recommended</div>}
                <div className="pricing-card-header">
                  <h3>{plan.name}</h3>
                  <div className="pricing-price">
                    <span className="price-dollar">$</span>
                    <span className="price-num">{plan.price}</span>
                    <span className="price-period">/month*</span>
                  </div>
                </div>
                <div className="pricing-card-body">
                  <ul className="pricing-features">
                    {plan.features.map((f, fi) => (
                      <li key={fi}><FaCheck className="pricing-check" /> {f}</li>
                    ))}
                  </ul>
                  <p className="pricing-desc">{plan.desc}</p>
                  <a href="/#contact" className="btn pricing-btn">{plan.cta}</a>
                </div>
              </div>
            ))}
          </div>
          
          <p className="pricing-disclaimer">
            <em>* Subject to Discovery Session</em>
          </p>
        </div>
      </section>

      <TroubleSection />
      <Footer />
    </div>
  );
};

export default PricingPage;
